import { useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Alert, Box, Button, CircularProgress, Stack, ThemeProvider } from '@mui/material';
import { PlusIcon } from '@phosphor-icons/react';

import { useBlocks } from '@/hooks/use-page-editor';
import { createUnitSiteTheme, resolveButtonFontKey, useGoogleFonts } from '@/styles/theme/create-unit-site-theme';
import { DEFAULT_THEME_CONFIG } from '@/lib/site-styles/theme-presets';
import { isEffectivelyPublic } from '@/lib/content-visibility';
import { SiteOrgProvider } from '@/lib/site-org-context';
import type { Block, BlockType, Page, Site } from '@/types/database';
import { AddBlockDialog } from '@/components/editor/AddBlockDialog';
import { BlockEditPanel } from '@/components/editor/BlockEditPanel';
import type { StudioViewState } from './StudioTopBar';
import { GridRow } from './GridRow';

interface StudioCanvasProps {
  pageId: string | undefined;
  page: Page | null;
  site: Site | null;
  canManage: boolean;
  viewState: StudioViewState;
}

function groupIntoRows(blocks: Block[]): Block[][] {
  const rows = new Map<number, Block[]>();
  for (const block of blocks) {
    const row = rows.get(block.row_order) ?? [];
    row.push(block);
    rows.set(block.row_order, row);
  }
  return Array.from(rows.entries())
    .sort(([a], [b]) => a - b)
    .map(([, rowBlocks]) => rowBlocks.sort((a, b) => a.block_order - b.block_order));
}

// The live editor canvas: renders each block through the same BlockRenderer
// the public site uses (wrapped in the unit's own theme) so what an admin
// edits here is exactly what a visitor sees, rather than a disconnected
// form. Blocks are grouped into 12-column-grid rows (row_order) — see
// GridRow for the side-by-side layout and column-resize interaction.
export function StudioCanvas({ pageId, page, site, canManage, viewState }: StudioCanvasProps) {
  const {
    blocks: allBlocks,
    loading,
    error,
    updateBlockContent,
    updateBlockVisibility,
    deleteBlock,
    moveRow,
    reorderRow,
    resizeRow,
    addBlock,
    addBlockToRow,
  } = useBlocks(pageId);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  // Selecting a block (outline + toolbar) no longer implicitly opens the
  // edit drawer — most edits now happen directly in the canvas (see
  // InlineTextBlock); the drawer only opens via an explicit "Edit" click,
  // tracked separately here.
  const [editPanelBlockId, setEditPanelBlockId] = useState<string | null>(null);
  // null: dialog closed. 'new-row': the persistent "Add Block" button,
  // always appends a full-width row. A block id: the hover "+" affordance
  // on that block, splits its row with the new block.
  const [addBlockTarget, setAddBlockTarget] = useState<'new-row' | string | null>(null);

  const themeConfig = site?.theme_config ?? DEFAULT_THEME_CONFIG;
  const theme = useMemo(() => createUnitSiteTheme(themeConfig), [themeConfig]);
  useGoogleFonts([themeConfig.fonts.headingFont, themeConfig.fonts.paragraphFont, resolveButtonFontKey(themeConfig.fonts), themeConfig.fonts.miscFont]);

  const siteStatus = site?.status ?? 'draft';
  // 'published' view is a read-only rendering of exactly what a public
  // visitor sees right now — same predicate as the public_read_published
  // RLS policy (see isEffectivelyPublic) — not a separate editing surface.
  const pageIsLive = page ? isEffectivelyPublic(page, null, siteStatus) : false;
  const blocks = useMemo(
    () =>
      viewState === 'published'
        ? page && pageIsLive
          ? allBlocks.filter((block) => isEffectivelyPublic(page, block, siteStatus))
          : []
        : allBlocks,
    [viewState, page, pageIsLive, allBlocks, siteStatus]
  );
  const effectiveCanManage = viewState === 'draft' && canManage;

  const rows = useMemo(() => groupIntoRows(blocks), [blocks]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      void reorderRow(active.id as string, over.id as string);
    }
  };

  const editPanelBlock = blocks.find((block) => block.id === editPanelBlockId) ?? null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (viewState === 'published' && !pageIsLive) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        This page isn&apos;t public yet — it&apos;s either unpublished or marked Internal in Page Settings.
      </Alert>
    );
  }

  return (
    <>
      {/* No border/card wrapper — the canvas renders blocks exactly as they
          appear on the published site (see SitePageChrome's <main>), so
          switching to the Published view state is a true rendering of the
          live page, not a lookalike that can drift.
          Only the block preview itself renders in the unit's own public-site
          theme (the whole point of a live WYSIWYG canvas) — the edit drawer
          is operator UI and must stay in the platform's own app theme, not
          whatever colors/font a unit has picked for their public site. */}
      <ThemeProvider theme={theme}>
       <SiteOrgProvider orgId={site?.clerk_org_id ?? null}>
        <Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
              <Stack>
                {rows.map((rowBlocks, index) => (
                  <GridRow
                    key={rowBlocks[0].id}
                    blocks={rowBlocks}
                    basePath=""
                    canManage={effectiveCanManage}
                    selectedBlockId={selectedBlockId}
                    isFirstRow={index === 0}
                    isLastRow={index === rows.length - 1}
                    onSelectBlock={setSelectedBlockId}
                    onEditBlock={setEditPanelBlockId}
                    onMoveRowUp={(blockId) => void moveRow(blockId, 'up')}
                    onMoveRowDown={(blockId) => void moveRow(blockId, 'down')}
                    onDeleteBlock={(blockId) => {
                      void deleteBlock(blockId);
                      if (selectedBlockId === blockId) setSelectedBlockId(null);
                      if (editPanelBlockId === blockId) setEditPanelBlockId(null);
                    }}
                    onAddToRow={setAddBlockTarget}
                    onResize={(leftId, rightId, leftSpan, rightSpan) => void resizeRow(leftId, rightId, leftSpan, rightSpan)}
                    onUpdateBlockContent={(blockId, content) => void updateBlockContent(blockId, content)}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
          {blocks.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>
              This page has no content blocks yet.
            </Alert>
          ) : null}
        </Box>
       </SiteOrgProvider>
      </ThemeProvider>

      {effectiveCanManage ? (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<PlusIcon size={16} />}
          onClick={() => setAddBlockTarget('new-row')}
          sx={{ mt: 2, borderStyle: 'dashed' }}
        >
          Add Block
        </Button>
      ) : null}

      <BlockEditPanel
        block={editPanelBlock}
        pageVisibility={page?.visibility}
        onClose={() => setEditPanelBlockId(null)}
        onUpdate={(blockId, content) => void updateBlockContent(blockId, content)}
        onUpdateVisibility={effectiveCanManage ? (blockId, visibility) => void updateBlockVisibility(blockId, visibility) : undefined}
      />

      <AddBlockDialog
        open={addBlockTarget !== null}
        onOpenChange={(open) => {
          if (!open) setAddBlockTarget(null);
        }}
        onAddBlock={(blockType: BlockType) => {
          if (addBlockTarget === 'new-row') void addBlock(blockType);
          else if (addBlockTarget) void addBlockToRow(addBlockTarget, blockType);
          setAddBlockTarget(null);
        }}
      />
    </>
  );
}
